// src/pages/Home.js
import React, { useEffect } from "react";
import "../App.css";
import { useInView } from "../hooks/useInView";
import { useNavigate } from "react-router-dom";
import { useCareModal } from "../context/CareModalContext";

const Home = () => {
  const navigate = useNavigate();
  const { openBookCare, openJoinTeam } = useCareModal();

  const [whoRef, whoInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [testimonialsRef, testimonialsInView] = useInView();

  useEffect(() => {
    const heroEl = document.querySelector(".home-hero");
    if (!heroEl) return;

    const handleScroll = () => {
      const offset = window.scrollY * 0.3;
      heroEl.style.backgroundPositionY = `${-offset}px`;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* HERO */}
      <section id="home" className="hero home-hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">
            Compassionate, Reliable Care You Can Trust.
          </h1>

          <p className="hero-subtitle">
            Supporting vulnerable adults with dignity, compassion, and
            consistent care.
          </p>

          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={openBookCare}>
              Book For Care
            </button>

            <button className="btn btn-light" onClick={openJoinTeam}>
              Join Our Team
            </button>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section
        id="about"
        ref={whoRef}
        className={`section who-we-are fade-up-section ${
          whoInView ? "in-view" : ""
        }`}
      >
        <h2 className="section-title">Who We Are</h2>
        <p className="section-subtitle">
          Dedicated to providing compassionate, reliable care for vulnerable
          adults.
        </p>

        <div className="who-content">
          <div className="who-image-wrapper">
            <img
              className="who-image"
              src="https://images.pexels.com/photos/7551583/pexels-photo-7551583.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Elderly hand being held"
            />
          </div>

          <div className="who-text">
            <p>
              At Ndils Daily Care, we believe every person deserves dignity,
              safety, and consistency. Our trained carers work closely with
              families and communities to deliver trusted, professional care.
            </p>

            <button
              className="btn btn-outline"
              onClick={() => navigate("/about")}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section id="services" ref={servicesRef} className="section services">
        <h2 className="section-title">Our Services</h2>

        <div className="services-grid">
          <article
            className={`service-card stagger-item ${
              servicesInView ? "in-view" : ""
            }`}
            style={{ "--delay": "0s" }}
          >
            <div className="service-icon">🏠</div>
            <h3>Domiciliary Care</h3>
            <p>Support with personal care, meals, medication, and more.</p>
          </article>

          <article
            className={`service-card stagger-item ${
              servicesInView ? "in-view" : ""
            }`}
            style={{ "--delay": "0.12s" }}
          >
            <div className="service-icon">🤝</div>
            <h3>Supported Living</h3>
            <p>Helping individuals live independently with daily assistance.</p>
          </article>

          <article
            className={`service-card stagger-item ${
              servicesInView ? "in-view" : ""
            }`}
            style={{ "--delay": "0.24s" }}
          >
            <div className="service-icon">💬</div>
            <h3>Companionship</h3>
            <p>Social visits and emotional support for vulnerable adults.</p>
          </article>
        </div>

        <div className="services-cta">
          <button
            className="btn btn-pill"
            onClick={() => navigate("/services")}
          >
            <span>Explore All Our Services</span>
            <span className="arrow">➜</span>
          </button>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section why-choose">
        <h2 className="section-title">Why Choose Us</h2>
        <p className="section-subtitle">
          The Care and Commitment That Sets Us Apart
        </p>

        <div className="why-grid">
          <div className="why-item">
            <div className="why-icon">🛡️</div>
            <h4>Safety</h4>
          </div>
          <div className="why-item">
            <div className="why-icon">🔁</div>
            <h4>Consistency</h4>
          </div>
          <div className="why-item">
            <div className="why-icon">💙</div>
            <h4>Compassion</h4>
          </div>
          <div className="why-item">
            <div className="why-icon">🎓</div>
            <h4>Training</h4>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section testimonials" ref={testimonialsRef}>
        <h2 className="section-title">Testimonials</h2>

        <div className="testimonials-grid">
          {[
            {
              name: "Sarah Thompson",
              role: "Daughter of Client",
              text:
                "The carers from Ndils Daily Care have been an absolute blessing. They treat my mother with such kindness and patience.",
            },
            {
              name: "James Patel",
              role: "Family Member",
              text:
                "Reliable, professional, and genuinely compassionate. They go above and beyond every day.",
            },
            {
              name: "Margaret Collins",
              role: "Care Recipient",
              text:
                "Their carers are respectful and well-trained — I've never felt safer or more supported.",
            },
          ].map((t, index) => (
            <article
              key={t.name}
              className={`testimonial-card stagger-item ${
                testimonialsInView ? "in-view" : ""
              }`}
              style={{ "--delay": `${index * 0.15}s` }}
            >
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">“{t.text}”</p>
              <p className="testimonial-name">{t.name}</p>
              <p className="testimonial-role">{t.role}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section id="contact" className="section contact-cta">
        <h2 className="section-title">Contact Us</h2>
        <p className="section-subtitle">Need Care? Contact Us Today!</p>

        <button
          className="btn btn-primary contact-btn"
          onClick={() => navigate("/contact")}
        >
          Contact Us
        </button>
      </section>
    </>
  );
};

export default Home;
